import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Card,
  CardBody,
  CardHeader,
  Badge,
  FormGroup,
  Label,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdLogsService from "../../../services/OrderingServices/AdLogsService";
import FilterCard from "../component/FilterCard";
import ExportToExcel from "../../../Components/Common/ExportToExcel";
import {
  formatDate,
  formatDateTimeWithSeconds,
  getTimeOnly,
} from "../../../Components/Common/FormatDate";

const AdLogsList = () => {
  const navigate = useNavigate();
  const [adLogsList, setAdLogsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [batchList, setBatchList] = useState([]);
  const [filters, setFilters] = useState({
    adId: "",
    macId: "",
    screenName: "",
    fromDate: "",
    toDate: "",
    advertiserId: "",
    campaignId: "",
    organizationId: "",
    name: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: { style: { fontSize: "0.9rem" } },
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  async function fetchAdLogsList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);

      const response = await AdLogsService.getAdLogs({
        adId: currentFilters.adId || undefined,
        macId: currentFilters.macId || undefined,
        fromDate: currentFilters.fromDate
          ? new Date(currentFilters.fromDate).toISOString()
          : undefined,
        toDate: currentFilters.toDate
          ? new Date(currentFilters.toDate).toISOString()
          : undefined,
        pageNumber: page,
        pageSize: size,
        screenName: currentFilters.screenName || undefined,
        advertiserId: currentFilters.advertiserId || undefined,
        campaignId: currentFilters.campaignId || undefined,
      });
      console.log("🚀 ~ fetchAdLogsList ~ response:", response);

      if (response) {
        setAdLogsList(response.data || []);
        setTotalRows(response.totalRecords || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch ad logs");
    } finally {
      setLoading(false);
    }
  }

  async function fetchBatchList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);

      const response = await AdLogsService.getAdLogsBatchList({
        macId: currentFilters.macId || undefined,
        batchCode: currentFilters.batchCode || undefined,
        fromDate: currentFilters.fromDate || undefined,
        toDate: currentFilters.toDate || undefined,
        pageNumber: page,
        pageSize: size,
      });

      if (response) {
        setBatchList(response.data || []);
        setTotalRows(response.totalRecords || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch batch list");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchBatchList(currentPage, pageSize);
  }, [currentPage, pageSize, debouncedFilters]);

  useEffect(() => {
    fetchAdLogsList(currentPage, pageSize);
  }, [currentPage, pageSize, debouncedFilters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      adId: "",
      macId: "",
      screenName: "",
      fromDate: "",
      toDate: "",
      advertiserId: "",
      campaignId: "",
      organizationId: "",
      name: "",
    });
    setCurrentPage(1);
  };

  const handleBatchClick = async (batchCode) => {
    // Navigate to AdLogsDetailTable with batch code filter
    navigate(`/ad-logs-detail-table?batchCode=${encodeURIComponent(batchCode)}`);
  };

  // Transform batch list data for Excel export
  const transformDataForExport = (data) => {
    return data.map((row, index) => ({
      "SN": index + 1,
      "Batch Code": row.batchCode || "-",
      "MAC ID": row.macId || "-",
      "Device Name": row.deviceName || "-",
      "Organization": row.organizationName || "-",
      "Date": row.batchStartDate ? formatDate(new Date(row.batchStartDate)) : "-",
      "Start Time": row.batchStartDate
        ? new Date(row.batchStartDate).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-",
      "End Time": row.batchEndDate
        ? new Date(row.batchEndDate).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-",
      "Total Logs": row.totalLogs ?? 0,
    }));
  };

  // Fetch function for export
  const fetchBatchListForExport = async (params) => {
    const response = await AdLogsService.getAdLogsBatchList({
      macId: params.macId || undefined,
      batchCode: params.batchCode || undefined,
      fromDate: params.fromDate || undefined,
      toDate: params.toDate || undefined,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    });
    return response;
  };

  const columns = [
    {
      name: "SN",
      cell: (row, index) => (
        <div>{(currentPage - 1) * pageSize + index + 1}</div>
      ),
      width: "60px",
      sortable: false,
    },

    {
      name: "Batch Code",
      selector: (row) => row.batchCode || "-",
      cell: (row) => (
        <div>
          <span
            style={{ cursor: "pointer", color: "#0d6efd" }}
            onClick={() => handleBatchClick(row.batchCode)}
          >
            {row.batchCode || "-"}
          </span>
        </div>
      ),
      sortable: true,
      width: "150px",
    },

    {
      name: "MAC ID",
      selector: (row) => row.macId || "-",
      sortable: true,
      width: "180px",
    },

    {
      name: "Device Name",
      selector: (row) => row.deviceName || "-",
      sortable: true,
    },

    {
      name: "Organization",
      selector: (row) => row.organizationName || "-",
      sortable: true,
    },
    {
      name: "Date",
      cell: (row) => {
        if (!row.batchStartDate) return "-";

        const date = new Date(row.batchStartDate);

        return formatDate(date);
      },
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "Time",
      cell: (row) => {
        if (!row.batchStartDate || !row.batchEndDate) return "-";

        const start = new Date(row.batchStartDate);
        const end = new Date(row.batchEndDate);

        return (
          <div className="text-muted">
            {start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
            <span className="mx-1">–</span>
            {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        );
      },
      sortable: false,
      minWidth: "160px",
    },

    {
      name: "Total Logs",
      selector: (row) => row.totalLogs ?? 0,
      sortable: true,
      width: "150px",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Ad Logs" pageTitle="Ad Management" />

          {/* Filter Card */}
          <FilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
          />

          {/* Data Table */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Ad Logs{" "}
                      <small className="text-muted">({totalRows} total)</small>
                    </h5>
                    <ExportToExcel
                      fetchDataFunction={fetchBatchListForExport}
                      filters={debouncedFilters}
                      fileName="Ad_Logs_Batch_List"
                      dataTransformer={transformDataForExport}
                      buttonText="Export"
                      buttonSize="sm"
                      buttonColor="success"
                    />
                  </div>
                </CardHeader>
                <CardBody>
                  {loading && !adLogsList.length ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Triangle color="#435ebe" height={80} width={80} />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={batchList}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={pageSize}
                        paginationRowsPerPageOptions={[10, 20, 50, 100, 1000]}
                        onChangePage={(page) => setCurrentPage(page)}
                        onChangeRowsPerPage={(newSize, page) => {
                          setPageSize(newSize);
                          setCurrentPage(1);
                        }}
                        progressPending={loading}
                        customStyles={customStyles}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdLogsList;
